import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon from '@op/components/BubbleCommon';
import { channelInfo } from '@op/components/ChannelLabel';
import { openWindowUrl } from '@op/utils/url';
import React, { useCallback, useMemo } from 'react';
import { Bubble } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import { accountKeyTitleList, SPECIAL_ACCOUNT_KEY } from '../config';
import { LocaleType } from '@op/types/common';
import { monitorLocale } from '@op/components/FilterCommon/constants/monitor';
import { NO_DATE } from '@op/constants';
import {
  AccountItemType,
  getAccountValue,
  getExternalValue,
  localSplitText,
  serversSplitText,
} from '@op/components/FilterCommon/utils/monitorUtil';
import _ from 'lodash-es';
import useLanguage from '@op/hooks/useLanguage';
import { MonitorRuleType, ruleOptionsMap } from '../ContentHeader';

export type AllMonitorType = {
  all: Array<string>;
  public: Array<string>;
  uncheck: Array<string>;
};

function getAllMonitorList(): AllMonitorType {
  return {
    all: [],
    public: [],
    uncheck: [],
  };
}

/**
 * 判断两个数组忽略顺序后是否相等
 */
const isEqualIgnoreOrder = (a: any[], b: any[]) => {
  const uniqueSortedA = _.sortBy(_.uniq(a));
  const uniqueSortedB = _.sortBy(_.uniq(b));
  if (uniqueSortedA.length !== uniqueSortedB.length) return false;
  return _.isEqual(uniqueSortedA, uniqueSortedB);
};

/**
 * 从 monitoringOptions 中提取官号分组映射
 * @returns groupMap: 一级key → { all: 二级value数组 }; accountToGroup: 二级value → 一级key
 */
export function buildAccountGroupMap(
  monitoringOptions: Array<{
    value: string;
    children: Array<{ value: string; originValue: string }>;
  }>,
): {
  groupMap: Record<string, { all: string[] }>;
  accountToGroup: Record<string, string>;
} {
  const groupMap: Record<string, { all: string[] }> = {};
  const accountToGroup: Record<string, string> = {};

  monitoringOptions.forEach((option) => {
    // 排除 keyword 和 external 分类（它们不参与 account 分组处理）
    if (
      option.value === SPECIAL_ACCOUNT_KEY.KEYWORD ||
      option.value === SPECIAL_ACCOUNT_KEY.EXTERNAL
    ) {
      return;
    }

    const children = option.children || [];
    if (children.length === 0) return;

    groupMap[option.value] = {
      all: children.map((child: any) => child.value),
    };

    children.forEach((child: any) => {
      accountToGroup[child.value] = option.value;
    });
  });

  return { groupMap, accountToGroup };
}

/**
 * 按一级分组处理官号选中数据
 */
function dealwithAccountGrouped(
  selectedAccounts: string[],
  allAccount: AllMonitorType,
  groupMap: Record<string, { all: string[] }>,
  accountToGroup: Record<string, string>,
): {
  account_categories: string[];
  social_media_by_account: string[];
  except_social_media_by_account: string[];
} {
  const account_categories: string[] = [];
  const social_media_by_account: string[] = [];
  const except_social_media_by_account: string[] = [];

  // ─── Step 1: 优先处理特殊值 ───
  // 没有官号选项时，视为全选（空集的全选即空），避免输出 -99999（表示"未选"）
  // 全部官号全选
  if (selectedAccounts.length === allAccount.all.length || allAccount.all.length === 0) {
    return {
      account_categories: [],
      social_media_by_account: ['99999'],
      except_social_media_by_account: [],
    };
  }

  // 没有选中任何官号
  if (selectedAccounts.length === 0) {
    return {
      account_categories: [],
      social_media_by_account: ['-99999'],
      except_social_media_by_account: [],
    };
  }

  // 仅公开数据全选
  if (isEqualIgnoreOrder(selectedAccounts, allAccount.public)) {
    return {
      account_categories: [],
      social_media_by_account: ['public'],
      except_social_media_by_account: [],
    };
  }

  // ─── Step 2: 非特殊值，按分组逐组处理 ───
  // 将已选中的 account 按分组归类
  const selectedByGroup: Record<string, string[]> = {};
  selectedAccounts.forEach((acc) => {
    const group = accountToGroup[acc];
    if (group) {
      if (!selectedByGroup[group]) selectedByGroup[group] = [];
      selectedByGroup[group].push(acc);
    } else {
      // 未找到所属分组（兜底），直接放入 social_media_by_account
      social_media_by_account.push(acc);
    }
  });

  // 遍历每个官号分组
  Object.keys(groupMap).forEach((groupKey) => {
    const allInGroup = groupMap[groupKey].all;
    const selected = selectedByGroup[groupKey] || [];

    if (allInGroup.length === 0) return; // 空分组跳过

    // 2.1: 该分组内二级全选 → 只加入 account_categories
    if (selected.length === allInGroup.length) {
      account_categories.push(groupKey);
      return;
    }

    // 2.2.1: 选中超过分组一半 → account_categories + 排除未选中的
    if (selected.length > allInGroup.length / 2) {
      account_categories.push(groupKey);
      allInGroup.forEach((v) => {
        if (!selected.includes(v)) {
          except_social_media_by_account.push(v);
        }
      });
      return;
    }

    // 2.2.2: 选中不超过一半 → 直接放入 social_media_by_account
    if (selected.length > 0) {
      social_media_by_account.push(...selected);
    }
  });

  // 如果 account_categories 覆盖了所有分组，简化为 99999 + 排除
  const allGroupKeys = Object.keys(groupMap).filter(
    (k) => groupMap[k].all.length > 0,
  );
  if (account_categories.length === allGroupKeys.length && allGroupKeys.length > 0) {
    return {
      account_categories: [],
      social_media_by_account: ['99999'],
      except_social_media_by_account,
    };
  }

  return { account_categories, social_media_by_account, except_social_media_by_account };
}

export function useSelectOptions({
  filterOptions,
  onlyAccount,
  isDiscordPrivate,
  isNightingale,
}: {
  filterOptions: {
    monitor_by_account_category: {
      history: Array<string>;
      monitory: {
        'discussion-groups': Array<AccountItemType>;
        'kol-monitoring': Array<AccountItemType>;
        'live-broadcast': Array<AccountItemType>;
        'official-accounts': Array<AccountItemType>;
        'single-post-comments': Array<AccountItemType>;
        [SPECIAL_ACCOUNT_KEY.PRIVATE]: Array<AccountItemType>;
        [SPECIAL_ACCOUNT_KEY.KEYWORD]: Array<string>;
        [SPECIAL_ACCOUNT_KEY.EXTERNAL]: Array<string>;
        [key: string]: Array<AccountItemType> | Array<string>;
      };
    };
    monitor_by_keyword: {
      history: Array<string>;
      monitoring: Array<string>;
      uncheck_monitoring: Array<string>;
    };
    keyword_mutual_account_config: boolean;
  };
  onlyAccount: boolean;
  isDiscordPrivate: boolean;
  isNightingale: boolean;
}) {
  const { formatMessage } = useIntl();
  function getTipsInfo(showTips = true) {
    if (!showTips) return null;
    return (
      <BubbleCommon
        placement="top"
        childStyle={{ verticalAlign: 'middle' }}
        tipsMap={{ en: 'Non-official accounts data', zh: '非官号数据' }}
      />
    );
  }

  function getKeywordOptions({
    value,
    textInfo,
    showTips,
    key,
  }: {
    key: string;
    value: Array<string>;
    textInfo: { text: string; textCN: string };
    showTips: boolean;
  }) {
    const local = getLocale();
    return {
      ...textInfo,
      render: (
        <>
          {local === 'en-US' ? textInfo.text : textInfo.textCN}
          {getTipsInfo(showTips)}
        </>
      ),
      value: key,
      children: value.map((keyword: any) => {
        const keyInfo = keyword?.toLocaleLowerCase?.();
        return {
          text: keyword,
          textCN: keyword,
          value: keyInfo,
          originValue: keyInfo,
        };
      }),
    };
  }

  function getExternalOptions({
    value,
    textInfo,
    key,
  }: {
    key: string;
    value: Array<AccountItemType>;
    textInfo: { text: string; textCN: string };
  }) {
    const local = getLocale();
    return {
      ...textInfo,
      render: <>{local === 'en-US' ? textInfo.text : textInfo.textCN}</>,
      value: key,
      children: (value || []).map((item) => {
        const value = getExternalValue(item);
        return {
          text: item?.account,
          textCN: item?.account,
          value,
          originValue: value,
        };
      }),
    };
  }

  function getItemElement(accountText: string, url: string, channel: any) {
    const icon = channelInfo(channel)?.img;
    return (
      <Bubble closeOnScroll dark placement="top" content={accountText}>
        <span className="dbo-monitor">
          <span className="dbo-monitor-text">
            {icon && (
              <img className="dbc-region-select-item-icon" src={icon} alt="" />
            )}
            <span className="dbc-region-select-item-text">{accountText}</span>
          </span>
          <SvgIcon
            className="dbo-monitor-icon"
            onClick={(e) => {
              openWindowUrl(url);
              e.stopPropagation();
              e.preventDefault();
            }}
            type="other-share-icon"
          />
        </span>
      </Bubble>
    );
  }

  function getAccountList(list: AccountItemType[]) {
    if (Array.isArray(list)) {
      return list.map((item) => getAccountValue(item));
    }
    return [];
  }

  const {
    options: monitoringOptions,
    allKeywordList: allKeyword,
    discordPrivateOptions,
    allAccountList: allAccount,
    allExternalList: allExternalList,
  } = useMemo(() => {
    let discordPrivateOptions: Array<string> = [];
    const allExternalList: AllMonitorType = getAllMonitorList();
    const allAccountList: AllMonitorType = getAllMonitorList();

    if (!filterOptions)
      return {
        options: [],
        allKeywordList: getAllMonitorList(),
        discordPrivateOptions,
        allAccountList,
        allExternalList,
      };
    const {
      monitor_by_account_category,
      monitor_by_keyword,
      keyword_mutual_account_config,
    } = filterOptions || {};
    const account = { ...(monitor_by_account_category?.monitory || {}) };
    const publicKeyword = monitor_by_keyword?.monitoring || [];
    const uncheckKeyword = monitor_by_keyword?.uncheck_monitoring || [];
    account[SPECIAL_ACCOUNT_KEY.KEYWORD] = [
      ...publicKeyword,
      ...uncheckKeyword,
    ];

    let keys = [...accountKeyTitleList];
    if (onlyAccount) {
      keys = keys.filter(({ isKeyword = false }) => !isKeyword);
    }

    const allKeywordList: AllMonitorType = {
      all: [],
      public: publicKeyword
        .map((item) => item.toLocaleLowerCase())
        .filter(Boolean),
      uncheck: uncheckKeyword
        .map((item) => item.toLocaleLowerCase())
        .filter(Boolean),
    };

    const options: Array<any> = keys
      .map(({ isKeyword, key, isPrivate, textInfo, isExternal }) => {
        const value = account[key];
        if (!value) return null;
        if (isKeyword) {
          const keywordValue = value as Array<string>;
          allKeywordList.all = keywordValue
            .map((keyword: string) => keyword?.toLocaleLowerCase?.())
            .filter(Boolean);
          return getKeywordOptions({
            value: keywordValue,
            textInfo,
            showTips: keyword_mutual_account_config,
            key,
          });
        }

        if (isExternal) {
          const externalValue = value as Array<AccountItemType>;
          allExternalList.all = externalValue
            .map((external) => {
              const value = getExternalValue(external);
              if (external.visibility !== 'uncheck') {
                allExternalList.public.push(value);
              } else {
                allExternalList.uncheck.push(value);
              }

              return value;
            })
            .filter(Boolean);
          return getExternalOptions({
            value: externalValue,
            textInfo,
            key,
          });
        }

        if (isPrivate) {
          discordPrivateOptions = getAccountList(value as any);
        }
        // 是private tab，并且是feeds，Nightingale类型游戏，然后不是私密渠道，就不显示
        if (isDiscordPrivate && isNightingale && !isPrivate) return null;
        // 不是private tab，并且是feeds，是Nightingale类型游戏，是私密渠道，就不显示
        if (!isDiscordPrivate && isNightingale && isPrivate) return null;

        return {
          ...textInfo,
          value: key,
          children: value.map((item: any) => {
            const { account, channel, url, visibility } = item;
            const accountInfo = getAccountValue(item);
            if (visibility !== 'uncheck') {
              allAccountList.public.push(accountInfo);
            } else {
              allAccountList.uncheck.push(accountInfo);
            }
            allAccountList.all.push(accountInfo);
            return {
              render: getItemElement(account, url, channel),
              text: account,
              textCN: account,
              value: accountInfo,
              originValue: accountInfo,
            };
          }),
        };
      })
      .filter(Boolean);

    return {
      options,
      allKeywordList,
      discordPrivateOptions,
      allAccountList,
      allExternalList,
    };
  }, [filterOptions, formatMessage]);

  return {
    monitoringOptions,
    allKeyword,
    discordPrivateOptions,
    allAccount,
    allExternalList,
  };
}

export function useButtonElement({
  lang,
  onMonitorChange,
  selectValue,
  onlyAccount,
  returnAllValue,
  allKeyword,
  allAccount,
  allExternalList,
  monitorRule,
  monitoringOptions,
}: {
  lang: LocaleType;
  onMonitorChange: (select: any) => { value: any };
  selectValue: any;
  onlyAccount: boolean;
  returnAllValue: boolean;
  allKeyword: AllMonitorType;
  allAccount: AllMonitorType;
  allExternalList: AllMonitorType;
  monitorRule: MonitorRuleType;
  monitoringOptions: Array<{ value: string; children: Array<{ value: string; originValue: string }> }>;
}) {
  const { formatMessage } = useLanguage();

  const { groupMap, accountToGroup } = useMemo(
    () => buildAccountGroupMap(monitoringOptions),
    [monitoringOptions],
  );

  function dealwith({
    value,
    isAll,
    data,
    key,
  }: {
    value: Array<string>;
    isAll: boolean;
    data: AllMonitorType;
    key: string;
  }) {
    if (isAll) {
      return { [key]: ['99999'] };
    }
    if (value.length === 0) {
      return { [key]: ['-99999'] };
    }
    const { all, public: publicValue, uncheck } = data;
    if (isEqualIgnoreOrder(value, publicValue)) return { [key]: ['public'] };

    // 后端不支持uncheck这个值
    // if (isEqualIgnoreOrder(value, uncheck)) return { [key]: ['uncheck'] };
    // console.log(value, all, "***********")
    if (value.length > all.length / 2) {
      return {
        [`except_${key}`]: all.filter((item) => !value.includes(item)),
        [key]: ['99999'],
      };
    }

    return { [key]: value };
  }

  const buttonElement = useCallback(
    (selected = selectValue, returnValue = false): any => {
      const { value: localSelectedValue } = onMonitorChange(selected);

      const result: {
        [key in 'account' | 'keyword' | 'external']: Array<string>;
      } = {
        account: [],
        keyword: [],
        external: [],
      };
      localSelectedValue.forEach((item: string) => {
        if (item.includes(serversSplitText)) {
          result.account.push(item);
        } else if (item.includes(localSplitText)) {
          const [, value] = item.split(localSplitText);
          result.external.push(value);
        } else {
          result.keyword.push(item);
        }
      });

      const allAccountLength = allAccount.all.length;
      const allKeywordLength = allKeyword.all.length;
      const allExternalLength = allExternalList.all.length;

      const accountLength = result.account?.length;
      const keywordLength = result.keyword?.length;
      const externalLength = result.external?.length;

      const keywordAll = allKeywordLength === keywordLength;
      const accountAll = allAccountLength === accountLength;
      const externalAll = allExternalLength === externalLength;
      const renderList = [];

      if (accountLength || accountAll) {
        renderList.push('Account', accountAll ? 'All' : accountLength);
      }
      if (keywordLength || keywordAll) {
        if (renderList.length !== 0) renderList.push('/');
        renderList.push('Keyword', keywordAll ? 'All' : keywordLength);
      }

      if (externalLength || externalAll) {
        if (renderList.length !== 0) renderList.push('/');
        renderList.push(
          'External Sources',
          externalAll ? 'All' : externalLength,
        );
      }

      let buttonEle: 'All' | 'None' | Array<string | number> = renderList;
      if (keywordAll && accountAll && externalAll) buttonEle = 'All';
      if (accountLength === 0 && keywordLength === 0 && externalLength === 0)
        buttonEle = 'None';

      if (onlyAccount && buttonEle !== 'All' && buttonEle !== 'None') {
        buttonEle.splice(2, 3);
      }

      if (returnValue) {
        // 返回所有值，就直接返回
        if (returnAllValue)
          return {
            data: {
              social_media_by_account: result.account,
              social_media_by_keyword: result.keyword,
              external_data_sources: result.external,
            },
            buttonEle,
            isAll: buttonEle === 'All',
            isNone: buttonEle === 'None',
          };

        // 官号使用分组处理
        const accountData = dealwithAccountGrouped(
          result.account,
          allAccount,
          groupMap,
          accountToGroup,
        );

        return {
          data: {
            account_categories: accountData.account_categories,
            social_media_by_account: accountData.social_media_by_account,
            ...(accountData.except_social_media_by_account.length > 0
              ? { except_social_media_by_account: accountData.except_social_media_by_account }
              : {}),
            // keyword 和 external 保持原有 dealwith() 逻辑
            ...dealwith({
              value: result.keyword,
              isAll: keywordAll,
              data: allKeyword,
              key: 'social_media_by_keyword',
            }),
            ...dealwith({
              value: result.external,
              isAll: externalAll,
              data: allExternalList,
              key: 'external_data_sources',
            }),
          },
          buttonEle,
          isAll: buttonEle === 'All',
          isNone: buttonEle === 'None',
        };
      }

      return buttonEle;
    },
    [selectValue, groupMap, accountToGroup],
  );

  const buttonElementMemo = useMemo(() => {
    const key = lang === 'zh-CN' ? 'cn' : 'en';
    const ele = buttonElement();
    return (
      <div>
        <span className="select-button-text">
          {monitorLocale[key].market_title}
          <span style={{ marginLeft: '4px' }}>
            ({formatMessage(ruleOptionsMap[monitorRule])})
          </span>
        </span>
        {typeof ele === 'string'
          ? formatMessage({ id: ele })
          : ele.map((item: any) =>
            formatMessage({ id: item || NO_DATE.EMPTY }),
          )}
      </div>
    );
  }, [buttonElement, lang, monitorRule]);

  return { buttonElementMemo, buttonElement };
}
